import { type ChangeEvent, type FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import HelpIcon from '@atlaskit/icon/core/question-circle';
import Toggle from '@atlaskit/toggle';
import { getBoardIdFromRoute } from '@trello/business-logic-react/board';
import { Button } from '@trello/nachos/button';
import { Tooltip } from '@trello/nachos/tooltip';

import { type SmartScheduleOnboardingAiRuleType } from '../useUpsertSmartScheduleOnboardingAiRule';
import { LazySmartScheduleOnboardingBoardSelector } from './LazySmartScheduleOnboardingBoardSelector';
import { SmartScheduleRuleSection } from './SmartScheduleRuleSection';

import * as styles from './SmartScheduleSettings.module.less';

interface SmartScheduleSettingsProps {
  onNext: () => void;
  selectedBoardAri: string | null;
  onBoardChange: (boardAri: string | null) => void;
  isProactiveEnabled: boolean;
  onProactiveToggle: () => void;
  secondaryRule: string;
  onSecondaryRuleChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  secondaryRuleType: SmartScheduleOnboardingAiRuleType;
  onSecondaryRuleTypeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onAddSecondaryRule: () => void;
  isSecondaryRuleSaved: boolean;
  onRuleTypeInfoClick: (ruleType: SmartScheduleOnboardingAiRuleType) => void;
}

export const SmartScheduleSettings: FunctionComponent<
  SmartScheduleSettingsProps
> = ({
  onNext,
  selectedBoardAri,
  onBoardChange,
  isProactiveEnabled,
  onProactiveToggle,
  secondaryRule,
  onSecondaryRuleChange,
  secondaryRuleType,
  onSecondaryRuleTypeChange,
  onAddSecondaryRule,
  isSecondaryRuleSaved,
  onRuleTypeInfoClick,
}) => {
  const { formatMessage } = useIntl();

  // Default to the board the user is currently on. The selector only applies
  // this when nothing is selected yet, so it never overrides a prior choice.
  const currentBoardId = getBoardIdFromRoute();

  return (
    <div className={styles.container}>
      <h2>
        <FormattedMessage
          id="templates.smart_schedule.onboarding.settings-heading"
          defaultMessage="Set your custom focus time rules"
          description="Heading on the custom focus time rules onboarding page"
        />
      </h2>
      <p>
        <FormattedMessage
          id="templates.smart_schedule.onboarding.rules-help-description"
          defaultMessage="These rules help determine when focus times will be scheduled for you."
          description="Explanation of how the entered rules affect scheduling"
        />
      </p>
      <div className={styles.formContainer}>
        <div className={styles.field}>
          <label
            className={styles.fieldLabel}
            htmlFor="smart-schedule-onboarding-board-select"
          >
            <FormattedMessage
              id="templates.smart_schedule.onboarding.select-board-label"
              defaultMessage="Select a board:"
              description="Label for the board selector in onboarding"
            />
          </label>
          <LazySmartScheduleOnboardingBoardSelector
            selectedBoardId={selectedBoardAri ?? undefined}
            defaultBoardId={currentBoardId}
            onChange={onBoardChange}
          />
        </div>
        <div className={styles.proactiveRow}>
          <span className={styles.proactiveLabel}>
            <label htmlFor="proactive-scheduling-toggle">
              <FormattedMessage
                id="templates.smart_schedule.onboarding.proactively-schedule-focus-times"
                defaultMessage="Proactively schedule focus times"
                description="Label for the proactive scheduling toggle"
              />
            </label>
            <Tooltip
              content={
                <FormattedMessage
                  id="templates.smart_schedule.onboarding.proactive-help-tooltip"
                  defaultMessage="Trello will look at your configured boards overnight and suggest focus times for applicable cards to help you plan your day"
                  description="Tooltip explaining what proactive focus-time scheduling does"
                />
              }
              position="top"
            >
              <span className={styles.helpIcon}>
                <HelpIcon
                  label={formatMessage({
                    id: 'templates.smart_schedule.onboarding.proactive-help-label',
                    defaultMessage:
                      'More information about proactive scheduling',
                    description:
                      'Accessible label for the proactive scheduling help icon',
                  })}
                />
              </span>
            </Tooltip>
          </span>
          <Toggle
            id="proactive-scheduling-toggle"
            size="large"
            isChecked={isProactiveEnabled}
            onChange={onProactiveToggle}
          />
        </div>
        <div className={styles.field}>
          <p className={styles.fieldLabel}>
            {secondaryRuleType === 'PLANNER_SCHEDULING' ? (
              <FormattedMessage
                id="templates.smart_schedule.onboarding.which-scheduling-label"
                defaultMessage="How would you like your focus times scheduled?"
                description="Label asking how the user's focus times should be scheduled"
              />
            ) : (
              <FormattedMessage
                id="templates.smart_schedule.onboarding.which-cards-label"
                defaultMessage="Which cards would you like focus time suggestions for?"
                description="Label asking which cards should get focus time suggestions"
              />
            )}
          </p>
          <SmartScheduleRuleSection
            rule={secondaryRule}
            onRuleChange={onSecondaryRuleChange}
            ruleType={secondaryRuleType}
            onRuleTypeChange={onSecondaryRuleTypeChange}
            onAddRule={onAddSecondaryRule}
            isRuleSaved={isSecondaryRuleSaved}
            onRuleTypeInfoClick={onRuleTypeInfoClick}
          />
        </div>
        <p className={styles.hint}>
          <FormattedMessage
            id="templates.smart_schedule.onboarding.add-rules-later-hint"
            defaultMessage="You can add more rules later in Planner settings."
            description="Hint that more rules can be added later in Planner settings"
          />
        </p>
      </div>
      <div className={styles.buttonContainer}>
        <Button appearance="primary" onClick={onNext}>
          <FormattedMessage
            id="templates.smart_schedule.onboarding.continue"
            defaultMessage="Continue"
            description="Button to continue to the next onboarding step"
          />
        </Button>
      </div>
    </div>
  );
};
